import type { TriageIndex } from '../../api/useTriageIndex';
import type { TriageRowCounts } from './triageMarkText';

/** The later of two ISO stamps; unparseable and absent both fall through. */
function laterOf(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  const ta = new Date(a).getTime();
  const tb = new Date(b).getTime();
  if (Number.isNaN(ta)) return b;
  if (Number.isNaN(tb)) return a;
  return tb > ta ? b : a;
}

/**
 * Join a row to the index (spec §6.6): the stuck-counter count is keyed on the
 * ROW'S RULE, the orphan count on the ROW'S PAIR.
 *
 * 🚨 `undefined` in, `undefined` out — an unreadable index must never be
 * flattened into zeroes, because zeroes render as a clean row.
 */
export function resolveTriageRowCounts(
  index: TriageIndex | undefined,
  keys: { ruleId?: string | null; pairId?: string | null }
): TriageRowCounts | undefined {
  if (!index) return undefined;
  const rule = keys.ruleId ? index.byRuleId?.[keys.ruleId] : undefined;
  const pair = keys.pairId ? index.byPairId?.[keys.pairId] : undefined;
  const counter = rule?.counter ?? 0;
  const orphan = pair?.orphan ?? 0;
  return {
    counter,
    orphan,
    // Only a bucket that actually contributes a mark may date it.
    latestAt: laterOf(
      counter > 0 ? rule?.latestAt ?? null : null,
      orphan > 0 ? pair?.latestAt ?? null : null
    )
  };
}
